/**
 * Footer styles — ported from ThemeMaker's
 * {@code lms-portal/src/components/shell/Footers.tsx}.
 *
 * <p>Three footer styles. Same {@link ShellConfig} drives all three;
 * styles differ in layout density and tagline/social treatment.
 *
 * <ul>
 *   <li>{@code classic}  — copyright left, link groups right (default)</li>
 *   <li>{@code compact}  — single thin line: copyright + inline links</li>
 *   <li>{@code expanded} — large multi-column layout with tagline + social</li>
 * </ul>
 *
 * <p>Localization: every authored string (group label, link label,
 * copyright, tagline, brand name) goes through
 * {@link t}{@code (value, locale, fallbackLocale)}.
 *
 * <p>Permissions: groups and individual links can carry
 * {@code requireAuth} / {@code requirePerms}. Hidden links don't
 * render. Groups whose links all became hidden — or which themselves
 * failed a gate — drop out entirely.
 */
import type { CSSProperties, ReactNode } from 'react';
import type { CurrentUser, ShellConfig } from '../../portal';
import { t, filterVisible, isVisible, DEFAULT_I18N } from '../../portal';
import { PortalLink } from '../../portal/PortalLink';
import { sbText } from '../../portal/i18nCatalogue';
import { StoreBadge, type StoreKind } from '../../portal/StoreBadge';
import { FooterResourceList } from '../widgets/FooterResourceList';

export interface FooterProps {
  config: ShellConfig;
  tenantHref: (href: string | undefined) => string;
  currentUser: CurrentUser | null;
  locale: string;
  /**
   * Optional tenant-private style resolver. Hosts that ship a theme
   * package with a custom footer (mirrors {@code resolveCustomHeader})
   * pass it here so unknown {@code style} values dispatch to the
   * theme's renderer instead of silently falling back to classic.
   * Return {@code null} for any style the resolver doesn't recognise.
   */
  resolveCustomFooter?: (style: string, props: FooterProps) => ReactNode | null;
}

/**
 * Whether the whole footer passes its {@code requireAuth} /
 * {@code requirePerms} gate — the "Visible to" control in the admin
 * Shell editor's Footer → Advanced group. Mirrors
 * {@code isHeaderVisible}. Absent config = visible.
 */
export function isFooterVisible(config: ShellConfig, currentUser: CurrentUser | null): boolean {
  const footer = config.footer;
  if (!footer) return true;
  return isVisible(
    {
      requireAuth: footer.requireAuth,
      requirePerms: footer.requirePerms,
      requireDynamicGroupIds: footer.requireDynamicGroupIds,
      requireSecurityGroupIds: footer.requireSecurityGroupIds,
    },
    currentUser,
  );
}

export function FooterRenderer(props: FooterProps) {
  if (!isFooterVisible(props.config, props.currentUser)) return null;
  const style = props.config.footer?.style ?? 'classic';
  switch (style) {
    case 'compact':
      return <CompactFooter {...props} />;
    case 'expanded':
      return <ExpandedFooter {...props} />;
    case 'minimal':
      return <MinimalFooter {...props} />;
    case 'classic':
      return <ClassicFooter {...props} />;
  }
  // Tenant-private or unknown style — delegate to the host resolver.
  // If the resolver returns null (or no resolver was supplied), fall
  // through to ClassicFooter rather than rendering nothing.
  const custom = props.resolveCustomFooter?.(style, props);
  if (custom !== null && custom !== undefined) return custom;
  return <ClassicFooter {...props} />;
}

// Was `var(--color-text-secondary)` which broke on tenants that
// override the footer to fixed-dark chrome (BTK in light mode):
// text-secondary stays in its mode-tuned variant (dark gray in light
// mode) regardless of the surface underneath.
// currentColor at 65% opacity follows whatever colour the footer's
// root cascaded — reads correctly in every theme/surface combination.
const muted: CSSProperties = {
  color: 'color-mix(in srgb, currentColor 65%, transparent)',
  fontSize: 'var(--font-size-sm, 14px)',
};

// All footer styles default to the same muted surface — the tone
// `expanded` has always used. `footer.background` overrides it.
// Chained through every theme's surface-naming convention (see
// packages/site-builder/blocks/src/_shared/surface.ts) rather than a
// single `var(--color-surface-muted, <hex>)` — themes that don't
// define `--color-surface-muted` at all (some use page/card/elevated
// naming, others base/raised/sunken) would otherwise fall straight to
// the literal hex fallback and never pick up a dark-mode value.
const DEFAULT_FOOTER_BG =
  'var(--color-surface-muted, var(--color-surface-subtle, var(--color-surface-sunken, #f1f5f9)))';

function footerBackground(config: ShellConfig): string {
  return config.footer?.background || DEFAULT_FOOTER_BG;
}

function footerTextColor(config: ShellConfig, styleDefault: string): string {
  return config.footer?.textColor || styleDefault;
}

// 'theme' (default) reads the same container token page content uses
// (--app-max-width) so the footer lines up with the page; 'full'
// stretches it edge-to-edge. Mirrors `containerRowStyle` in
// shells/headers/index.tsx.
function footerContentMaxWidth(config: ShellConfig): string {
  return config.footer?.maxWidth === 'full' ? 'none' : 'var(--app-max-width, 1200px)';
}

function ResourceListSlot({
  config,
  tenantHref,
  locale,
}: {
  config: ShellConfig;
  tenantHref: (href: string | undefined) => string;
  locale: string;
}) {
  const rl = config.footer?.resourceList;
  if (!rl?.enabled) return null;
  const fallback = (config.i18n ?? DEFAULT_I18N).default;
  return (
    <div
      style={{
        maxWidth: footerContentMaxWidth(config),
        margin: '0 auto',
        padding: 'var(--space-5, 20px) clamp(14px, 2.5vw, 24px) 0',
      }}
    >
      <FooterResourceList
        title={t(rl.title, locale, fallback)}
        source={rl.source ?? 'news'}
        limit={rl.limit}
        locale={locale}
        tenantHref={tenantHref}
        fallbackLogo={config.brand?.logo}
      />
    </div>
  );
}

function resolveGroups(
  config: ShellConfig,
  currentUser: CurrentUser | null,
  locale: string,
  fallback: string,
) {
  const raw = filterVisible(config.footer?.groups ?? [], currentUser);
  return raw
    .map((g) => ({
      label: t(g.label, locale, fallback),
      links: filterVisible(g.links ?? [], currentUser).map((l) => ({
        label: t(l.label, locale, fallback),
        href: l.href,
      })),
    }))
    .filter((g) => g.links.length > 0);
}

// =============================================================================
// SocialIcons — reusable circular social-media icon row
// =============================================================================
//
// Used by both compact and expanded footer styles so all footer
// variants render the same affordance from the same shape input.
//
// The glyph set is self-contained (the runtime package can't depend on a
// theme package) and covers the platforms the site-builder admin offers
// (twitter/x, linkedin, youtube, instagram, github, tiktok, letterboxd,
// discord, facebook, telegram, whatsapp). Unknown platforms fall back to a
// generic link glyph so a row never breaks or shows a bare letter.

// Official brand colors, used as the icon's circle background so the row
// reads as a set of recognizable brand marks rather than uniform grey dots.
// Instagram has no single brand color, so it gets its real four-stop
// gradient instead of a flattened approximation.
const PLATFORM_BRAND_BG: Record<string, string> = {
  facebook: '#1877F2',
  twitter: '#000000',
  x: '#000000',
  instagram: 'linear-gradient(45deg, #f09433, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888)',
  linkedin: '#0A66C2',
  youtube: '#FF0000',
  telegram: '#26A5E4',
  github: '#181717',
  tiktok: '#000000',
  discord: '#5865F2',
  whatsapp: '#25D366',
};

// Display names for the hover title / aria-label — the raw platform slug
// (e.g. "x", "tiktok") isn't presentable on its own.
const PLATFORM_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  twitter: 'X (Twitter)',
  x: 'X (Twitter)',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
  telegram: 'Telegram',
  github: 'GitHub',
  tiktok: 'TikTok',
  discord: 'Discord',
  whatsapp: 'WhatsApp',
};

function SocialGlyph({ platform, size = 16 }: { platform: string; size?: number }) {
  const p = platform.toLowerCase();
  const svg = { viewBox: '0 0 24 24', width: size, height: size, fill: 'currentColor' } as const;
  if (p === 'facebook')
    return (
      <svg {...svg} aria-hidden>
        <path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.2c-1.2 0-1.6.8-1.6 1.6V12h2.7l-.4 2.9h-2.3v7A10 10 0 0 0 22 12z" />
      </svg>
    );
  if (p === 'twitter' || p === 'x')
    return (
      <svg {...svg} aria-hidden>
        <g transform="translate(2,2) scale(0.833)">
          <path d="M18.9 2H22l-7.5 8.6L23 22h-6.9l-5.4-7-6.2 7H1.4l8-9.2L1 2h7l4.9 6.5L18.9 2zm-1.2 18h1.9L7.4 4H5.4l12.3 16z" />
        </g>
      </svg>
    );
  if (p === 'instagram')
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        width={size}
        height={size}
        aria-hidden
      >
        <rect x="2" y="2" width="20" height="20" rx="5" />
        <circle cx="12" cy="12" r="4.2" />
        <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none" />
      </svg>
    );
  if (p === 'linkedin')
    return (
      <svg {...svg} aria-hidden>
        <g transform="translate(2,2) scale(0.833)">
          <path d="M4.98 3.5A2.5 2.5 0 1 1 0 3.5a2.5 2.5 0 0 1 4.98 0zM.5 8h4V24h-4V8zm7.5 0h3.8v2.2h.1c.5-1 1.8-2.2 3.8-2.2 4 0 4.8 2.6 4.8 6V24h-4v-7.1c0-1.7 0-3.9-2.4-3.9s-2.7 1.8-2.7 3.7V24H8V8z" />
        </g>
      </svg>
    );
  if (p === 'youtube')
    return (
      <svg {...svg} aria-hidden>
        <path d="M23 7.5a3 3 0 0 0-2.1-2.1C19 4.9 12 4.9 12 4.9s-7 0-8.9.5A3 3 0 0 0 1 7.5 31 31 0 0 0 .5 12 31 31 0 0 0 1 16.5a3 3 0 0 0 2.1 2.1c1.9.5 8.9.5 8.9.5s7 0 8.9-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 23.5 12 31 31 0 0 0 23 7.5zM9.8 15.3V8.7l5.7 3.3-5.7 3.3z" />
      </svg>
    );
  if (p === 'telegram')
    return (
      <svg {...svg} aria-hidden>
        <path d="M21.9 4.3 18.6 19c-.2 1-.9 1.3-1.8.8l-4.9-3.6-2.4 2.3c-.3.3-.5.5-1 .5l.3-4.9 8.9-8c.4-.3-.1-.5-.6-.2L6.1 13 1.4 11.5c-1-.3-1-1 .2-1.5l18.9-7.3c.9-.3 1.6.2 1.4 1.6z" />
      </svg>
    );
  if (p === 'github')
    return (
      <svg {...svg} aria-hidden>
        <path d="M12 2a10 10 0 0 0-3.2 19.5c.5.1.7-.2.7-.5v-1.7c-2.8.6-3.4-1.3-3.4-1.3-.5-1.2-1.1-1.5-1.1-1.5-.9-.6.1-.6.1-.6 1 .1 1.5 1 1.5 1 .9 1.5 2.3 1.1 2.9.8.1-.6.3-1.1.6-1.4-2.2-.2-4.6-1.1-4.6-5a3.9 3.9 0 0 1 1-2.7c-.1-.3-.5-1.3.1-2.6 0 0 .8-.3 2.7 1a9.3 9.3 0 0 1 4.9 0c1.9-1.3 2.7-1 2.7-1 .6 1.3.2 2.3.1 2.6a3.9 3.9 0 0 1 1 2.7c0 3.9-2.3 4.8-4.6 5 .4.3.7.9.7 1.9v2.8c0 .3.2.6.7.5A10 10 0 0 0 12 2z" />
      </svg>
    );
  if (p === 'tiktok')
    return (
      <svg {...svg} aria-hidden>
        <path d="M16.5 2h-3v13.1a2.7 2.7 0 1 1-2-2.6V9.4a5.9 5.9 0 1 0 5 5.8V8.6a7.2 7.2 0 0 0 4 1.2V6.7a4.2 4.2 0 0 1-4-4.7z" />
      </svg>
    );
  if (p === 'discord')
    return (
      <svg {...svg} aria-hidden>
        <path d="M20 5.3A17 17 0 0 0 15.8 4l-.2.4a13 13 0 0 1 3.7 1.9 12 12 0 0 0-14.6 0A13 13 0 0 1 8.4 4.4L8.2 4A17 17 0 0 0 4 5.3 18.5 18.5 0 0 0 .9 18a17 17 0 0 0 5.1 2.6l.6-1a11 11 0 0 1-1.8-.9l.4-.3a12 12 0 0 0 10.6 0l.4.3a11 11 0 0 1-1.8.9l.6 1A17 17 0 0 0 23.1 18 18.4 18.4 0 0 0 20 5.3zM8.5 15.3c-1 0-1.8-.9-1.8-2s.8-2 1.8-2 1.8.9 1.8 2-.8 2-1.8 2zm7 0c-1 0-1.8-.9-1.8-2s.8-2 1.8-2 1.8.9 1.8 2-.8 2-1.8 2z" />
      </svg>
    );
  if (p === 'whatsapp')
    return (
      <svg {...svg} aria-hidden>
        <path d="M12 2a10 10 0 0 0-8.5 15.3L2 22l4.8-1.5A10 10 0 1 0 12 2zm5.8 14.2c-.2.7-1.4 1.3-2 1.4-.5.1-1.1.1-1.8-.1-.4-.1-1-.3-1.7-.6-3-1.3-4.9-4.3-5.1-4.5-.1-.2-1.2-1.6-1.2-3s.7-2.1 1-2.4c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.5.2.5.7 1.8.8 1.9.1.2.1.3 0 .5-.1.2-.1.3-.3.5-.1.2-.3.3-.4.5-.2.2-.3.3-.1.6.2.3.8 1.3 1.7 2.1 1.2 1 2.1 1.4 2.4 1.5.3.1.5.1.6-.1.2-.2.7-.8.9-1.1.2-.2.4-.2.6-.1l1.7.8c.2.1.4.2.4.3.1.2.1.9-.1 1.5z" />
      </svg>
    );
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      width={size}
      height={size}
      aria-hidden
    >
      <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1.5 1.5M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1.5-1.5" />
    </svg>
  );
}

function SocialIcons({
  social,
  size = 32,
}: {
  social: Array<{ platform: string; href?: string }>;
  size?: number;
}) {
  if (!social.length) return null;
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <style
        dangerouslySetInnerHTML={{
          __html: `
            .lms-social-icon {
              border: 1px solid transparent;
              transition: transform .15s ease, border-color .15s ease, filter .15s ease, box-shadow .15s ease;
            }
            .lms-social-icon:hover, .lms-social-icon:focus-visible {
              transform: translateY(-2px);
              filter: brightness(1.1);
              box-shadow: 0 4px 10px rgba(0, 0, 0, 0.18);
            }
            .lms-social-icon:active { transform: translateY(0); }
          `,
        }}
      />
      {social.map((s, i) => {
        const p = s.platform.toLowerCase();
        const brandBg = PLATFORM_BRAND_BG[p];
        const label = PLATFORM_LABELS[p] ?? s.platform;
        return (
          <PortalLink
            key={i}
            href={s.href}
            aria-label={label}
            title={label}
            className="lms-social-icon"
            style={{
              width: size,
              height: size,
              borderRadius: '50%',
              background: brandBg ?? 'var(--color-surface-page, #f7f8fa)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: brandBg ? '#fff' : 'var(--color-text-secondary, #5f6675)',
            }}
          >
            <SocialGlyph platform={s.platform} size={Math.round(size * 0.5)} />
          </PortalLink>
        );
      })}
    </div>
  );
}

// =============================================================================
// 1. ClassicFooter — copyright left, groups right
// =============================================================================

function ClassicFooter({ config, tenantHref, currentUser, locale }: FooterProps) {
  const footer = config.footer ?? {};
  const fallback = (config.i18n ?? DEFAULT_I18N).default;
  const groups = resolveGroups(config, currentUser, locale, fallback);
  const copyright = t(footer.copyright, locale, fallback);
  const social = (footer.social ?? []).filter(
    (s) => !!s && typeof s.platform === 'string' && s.platform.length > 0,
  );
  const hasContent =
    !!copyright || groups.length > 0 || social.length > 0 || !!footer.resourceList?.enabled;
  if (!hasContent) return null;
  return (
    <footer
      style={{
        background: footerBackground(config),
        color: footerTextColor(
          config,
          'var(--color-text-on-sunken, var(--color-text-primary, inherit))',
        ),
        borderTop:
          '1px solid var(--color-border-on-sunken, var(--color-border-default, var(--color-neutral-200, #e5e7eb)))',
      }}
    >
      <ResourceListSlot config={config} tenantHref={tenantHref} locale={locale} />
      <div
        style={{
          maxWidth: footerContentMaxWidth(config),
          margin: '0 auto',
          padding: 'var(--space-8, 32px) clamp(14px, 2.5vw, 24px)',
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 'var(--space-6, 24px)',
        }}
      >
        <div style={{ ...muted, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {copyright && <div>{copyright}</div>}
          {social.length > 0 && <SocialIcons social={social} />}
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-8, 32px)', flexWrap: 'wrap' }}>
          {groups.map((g, i) => (
            <div key={i}>
              <div
                style={{
                  fontSize: 'var(--font-size-xs, 12px)',
                  fontWeight: 600,
                  marginBottom: 6,
                }}
              >
                {g.label}
              </div>
              {g.links.map((l, j) => (
                <PortalLink
                  key={j}
                  href={tenantHref(l.href)}
                  style={{ ...muted, display: 'block' }}
                >
                  {l.label}
                </PortalLink>
              ))}
            </div>
          ))}
        </div>
      </div>
    </footer>
  );
}

// =============================================================================
// 2. CompactFooter — single line
// =============================================================================

function CompactFooter({ config, tenantHref, currentUser, locale }: FooterProps) {
  const footer = config.footer ?? {};
  const fallback = (config.i18n ?? DEFAULT_I18N).default;
  const groups = resolveGroups(config, currentUser, locale, fallback);
  const allLinks = groups.flatMap((g) => g.links);
  const copyright = t(footer.copyright, locale, fallback);
  const social = (footer.social ?? []).filter(
    (s) => !!s && typeof s.platform === 'string' && s.platform.length > 0,
  );
  const utility = (footer.utilityLinks ?? [])
    .filter((u) => !!u && (typeof u.label === 'string' || typeof u.label === 'object'))
    .map((u) => ({ label: t(u.label, locale, fallback), href: u.href }))
    .filter((u) => u.label);

  // Empty-state guard. If a tenant configures the compact style but
  // sets neither copyright NOR groups NOR social NOR utility links,
  // render nothing so the page doesn't end with an orphan border.
  const hasContent =
    !!copyright ||
    allLinks.length > 0 ||
    social.length > 0 ||
    utility.length > 0 ||
    !!footer.resourceList?.enabled;
  if (!hasContent) return null;

  return (
    <footer
      style={{
        background: footerBackground(config),
        color: footerTextColor(
          config,
          'var(--color-text-on-sunken, var(--color-text-primary, inherit))',
        ),
        borderTop:
          '1px solid var(--color-border-on-sunken, var(--color-border-default, var(--color-neutral-200, #e5e7eb)))',
      }}
    >
      <ResourceListSlot config={config} tenantHref={tenantHref} locale={locale} />
      <div
        style={{
          maxWidth: footerContentMaxWidth(config),
          margin: '0 auto',
          padding: 'var(--space-4, 16px) clamp(14px, 2.5vw, 24px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 'var(--space-3, 12px)',
          ...muted,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3, 12px)',
            flexWrap: 'wrap',
          }}
        >
          {copyright && <span>{copyright}</span>}
          {/* Utility links (legal / KVKK / cookies) — middot-separated. */}
          {utility.map((u, i) => (
            <span
              key={i}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-3, 12px)' }}
            >
              {(copyright || i > 0) && (
                <span aria-hidden style={{ opacity: 0.45 }}>
                  ·
                </span>
              )}
              <PortalLink href={tenantHref(u.href)} style={{ color: 'inherit' }}>
                {u.label}
              </PortalLink>
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4, 16px)' }}>
          {allLinks.map((l, i) => (
            <PortalLink key={i} href={tenantHref(l.href)} style={{ color: 'inherit' }}>
              {l.label}
            </PortalLink>
          ))}
          {social.length > 0 && <SocialIcons social={social} />}
        </div>
      </div>
    </footer>
  );
}

// =============================================================================
// 3. ExpandedFooter — tagline + multi-column + social row
// =============================================================================

function resolveImgSrc(
  v: string | { light?: string; dark?: string } | undefined,
  prefer: 'light' | 'dark' = 'light',
): string | undefined {
  if (!v) return undefined;
  if (typeof v === 'object') return prefer === 'dark' ? v.dark || v.light : v.light || v.dark;
  return v;
}

function ExpandedFooter({ config, tenantHref, currentUser, locale }: FooterProps) {
  const footer = config.footer ?? {};
  const rawBrand = config.brand ?? {};
  const brand = {
    ...rawBrand,
    logo: resolveImgSrc(
      rawBrand.logo as string | { light?: string; dark?: string } | undefined,
      'light',
    ),
    logoDark:
      resolveImgSrc(
        rawBrand.logoDark as string | { light?: string; dark?: string } | undefined,
        'dark',
      ) ||
      resolveImgSrc(
        rawBrand.logo as string | { light?: string; dark?: string } | undefined,
        'dark',
      ),
  };
  const fallback = (config.i18n ?? DEFAULT_I18N).default;
  const groups = resolveGroups(config, currentUser, locale, fallback);
  const groupCount = groups.length;
  // Resolve real values — empty stays empty. Empty-string fallback
  // masked deletions in ThemeMaker; same rule applies here.
  const brandName = t(brand.name, locale, fallback).trim();
  const hasLogo = !!(brand.logo || brand.logoDark);
  const hasName = brandName.length > 0;
  const tagline = t(footer.tagline, locale, fallback);
  const copyright = t(footer.copyright, locale, fallback);
  return (
    <footer
      style={{
        // Muted surface rather than surface-card: on light themes the
        // card token is pure white, so the footer melted into the page
        // and the top hairline was the only separation. surface-muted
        // is a half-step darker in light mode and stays dark in dark
        // mode, so the footer reads as its own band in both.
        background: footerBackground(config),
        color: footerTextColor(
          config,
          'var(--color-text-primary, var(--color-text-primary, inherit))',
        ),
        borderTop:
          '1px solid var(--color-border-on-sunken, var(--color-border-default, var(--color-neutral-200, #e5e7eb)))',
      }}
    >
      <div
        className="lms-footer-expanded-inner"
        style={{
          maxWidth: footerContentMaxWidth(config),
          margin: '0 auto',
          padding: 'clamp(24px, 4vw, 48px) clamp(14px, 2.5vw, 24px)',
        }}
      >
        <div
          className="lms-footer-expanded-grid"
          style={
            {
              display: 'grid',
              ['--lms-footer-cols' as string]: `2fr repeat(${Math.max(groupCount, 1)}, 1fr)`,
              gridTemplateColumns: `var(--lms-footer-cols)`,
              gap: 'clamp(20px, 3vw, 32px)',
            } as CSSProperties
          }
        >
          <div>
            {(hasLogo || hasName) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 8,
                }}
              >
                {hasLogo &&
                  (brand.logo && brand.logoDark ? (
                    <>
                      {/* Fixed-size box (both dimensions) reserved BEFORE the
                       *  image loads — a tenant logo's real aspect ratio is
                       *  unknown ahead of time, so pinning only `height`
                       *  (leaving width to resolve from the natural image)
                       *  still lets the row's width jump once it loads,
                       *  which shows up as layout shift under slow network.
                       *  `object-fit: contain` inside a reserved box gives a
                       *  zero-shift guarantee regardless of the real ratio,
                       *  at the cost of letterboxing an unusually wide/narrow
                       *  logo — an acceptable trade a slug of shift isn't. */}
                      <div className="lms-logo--light" style={{ width: 160, height: 46 }}>
                        <img
                          src={brand.logo}
                          alt={brandName || 'logo'}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            objectPosition: 'left center',
                          }}
                          decoding="async"
                        />
                      </div>
                      <div className="lms-logo--dark" style={{ width: 160, height: 46 }}>
                        <img
                          src={brand.logoDark}
                          alt={brandName || 'logo'}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            objectPosition: 'left center',
                          }}
                          decoding="async"
                        />
                      </div>
                    </>
                  ) : (
                    <div style={{ width: 160, height: 46 }}>
                      <img
                        src={brand.logo || brand.logoDark}
                        alt={brandName || 'logo'}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          objectPosition: 'left center',
                        }}
                        decoding="async"
                      />
                    </div>
                  ))}
                {hasName && (
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 'var(--font-size-lg, 18px)',
                      fontFamily: 'var(--font-family-display, inherit)',
                    }}
                  >
                    {brandName}
                  </div>
                )}
              </div>
            )}
            {tagline && <p style={{ ...muted, maxWidth: 360, margin: 0 }}>{tagline}</p>}
            {footer.social && footer.social.length > 0 && (
              <div style={{ marginTop: 'var(--space-4, 16px)' }}>
                <SocialIcons
                  social={footer.social.filter(
                    (s) => !!s && typeof s.platform === 'string' && s.platform.length > 0,
                  )}
                />
              </div>
            )}

            {footer.appBadges && footer.appBadges.length > 0 && (
              <div
                style={{
                  marginTop: 'var(--space-4, 16px)',
                  display: 'flex',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                {footer.appBadges
                  .filter((b) => b && typeof b.platform === 'string' && !!b.href)
                  .map((b, i) => (
                    <AppStoreBadge key={i} platform={b.platform} href={b.href} />
                  ))}
              </div>
            )}
          </div>
          {groups.map((g, i) => (
            <div key={i}>
              <div
                style={{
                  fontSize: 'var(--font-size-xs, 12px)',
                  fontWeight: 600,
                  marginBottom: 'var(--space-3, 12px)',
                  textTransform: 'uppercase',
                  letterSpacing: 'var(--font-tracking-wider, 0.05em)',
                }}
              >
                {g.label}
              </div>
              {g.links.map((l, j) => (
                <PortalLink
                  key={j}
                  href={tenantHref(l.href)}
                  style={{
                    ...muted,
                    display: 'block',
                    padding: 'var(--space-1, 4px) 0',
                  }}
                >
                  {l.label}
                </PortalLink>
              ))}
            </div>
          ))}
        </div>
        {copyright || footer.resourceList?.enabled ? (
          <div
            style={{
              marginTop: 'clamp(24px, 4vw, 48px)',
              paddingTop: 'var(--space-4, 16px)',
              // Hairline inside footer chrome. currentColor at 14% alpha
              // follows the footer's cascaded text color.
              borderTop: '1px solid color-mix(in srgb, currentColor 14%, transparent)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 'clamp(32px, 6vw, 64px)',
            }}
          >
            <div style={{ ...muted, flex: '1 1 280px', minWidth: 200 }}>{copyright}</div>
            {footer.resourceList?.enabled && (
              <div style={{ flex: '0 1 400px', minWidth: 280, maxWidth: 400 }}>
                <FooterResourceList
                  title={t(footer.resourceList.title, locale, fallback)}
                  source={footer.resourceList.source ?? 'news'}
                  limit={footer.resourceList.limit}
                  locale={locale}
                  tenantHref={tenantHref}
                  fallbackLogo={brand.logo}
                />
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Utility row — slim secondary strip for KVKK / cookie / terms. */}
      {footer.utilityLinks && footer.utilityLinks.length > 0 && (
        <div
          style={{
            // Sits on top of the footer's muted surface — a fixed
            // surface token here would land lighter than its parent on
            // light themes. currentColor at 6% deepens the strip in
            // light mode and lightens it in dark mode, so it always
            // reads as a step away from the footer body.
            background: 'color-mix(in srgb, currentColor 6%, transparent)',
            borderTop: '1px solid color-mix(in srgb, currentColor 14%, transparent)',
            padding:
              'var(--space-2-5, 10px) clamp(var(--space-3-5, 14px), 2.5vw, var(--space-6, 24px))',
          }}
        >
          <div
            style={{
              maxWidth: footerContentMaxWidth(config),
              margin: '0 auto',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px 16px',
              fontSize: 'var(--font-size-xs, 12px)',
              color: 'var(--color-text-tertiary, #9ca3af)',
            }}
          >
            {footer.utilityLinks
              .filter((u) => u && (typeof u.label === 'string' || typeof u.label === 'object'))
              .map((u, i, arr) => {
                const label = t(u.label, locale, fallback);
                if (!label) return null;
                return (
                  <span
                    key={i}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px 16px' }}
                  >
                    {u.href ? (
                      <PortalLink
                        href={tenantHref(u.href)}
                        style={{ color: 'inherit', textDecoration: 'none' }}
                      >
                        {label}
                      </PortalLink>
                    ) : (
                      <span>{label}</span>
                    )}
                    {i < arr.length - 1 && <span style={{ opacity: 0.4, marginLeft: 16 }}>·</span>}
                  </span>
                );
              })}
          </div>
        </div>
      )}
    </footer>
  );
}

// =============================================================================
// 4. MinimalFooter — single thin line, copyright + utility links only
// =============================================================================
//
// The lightest footer variant. No group columns, no social row, no
// logo, no app badges — just a centered copyright and an optional
// utility-links strip. Picked by tenants who want a barely-there
// footer (single-purpose landing portals).

function MinimalFooter({ config, tenantHref, currentUser, locale }: FooterProps) {
  const footer = config.footer ?? {};
  const fallback = (config.i18n ?? DEFAULT_I18N).default;
  const copyright = t(footer.copyright, locale, fallback);
  const utility = (footer.utilityLinks ?? [])
    .filter((u) => !!u && (typeof u.label === 'string' || typeof u.label === 'object'))
    .map((u) => ({ label: t(u.label, locale, fallback), href: u.href }))
    .filter((u) => u.label);

  // Honour visibility gates on utility links too — utilityLinks share
  // the requireAuth/requirePerms shape via filterVisible upstream when
  // the schema gains those fields. Today they're public-only.
  void currentUser;

  if (!copyright && utility.length === 0 && !footer.resourceList?.enabled) return null;

  return (
    <footer
      style={{
        background: footerBackground(config),
        color: footerTextColor(
          config,
          'var(--color-text-on-sunken, var(--color-text-primary, inherit))',
        ),
        borderTop:
          '1px solid var(--color-border-on-sunken, var(--color-border-default, var(--color-neutral-200, #e5e7eb)))',
      }}
    >
      <ResourceListSlot config={config} tenantHref={tenantHref} locale={locale} />
      <div
        style={{
          maxWidth: footerContentMaxWidth(config),
          margin: '0 auto',
          padding: 'var(--space-3, 12px) clamp(14px, 2.5vw, 24px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: 'var(--space-3, 12px)',
          fontSize: 'var(--font-size-xs, 12px)',
          ...muted,
        }}
      >
        {copyright && <span>{copyright}</span>}
        {utility.map((u, i) => (
          <span
            key={i}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-3, 12px)' }}
          >
            {(copyright || i > 0) && (
              <span aria-hidden style={{ opacity: 0.45 }}>
                ·
              </span>
            )}
            {u.href ? (
              <PortalLink href={tenantHref(u.href)} style={{ color: 'inherit' }}>
                {u.label}
              </PortalLink>
            ) : (
              <span>{u.label}</span>
            )}
          </span>
        ))}
      </div>
    </footer>
  );
}

// =============================================================================
// AppStoreBadge — inline SVG store badges
// =============================================================================
//
// Inline SVG, not hosted assets: Apple/Google occasionally change badge
// CDN URLs and break production sites; inlining keeps us online. Colour
// follows the surrounding text so they look right in dark and light
// without us shipping per-mode PNGs.

function AppStoreBadge({ platform, href }: { platform: string; href: string }) {
  // App Store / Google Play render the official themed badge artwork.
  const OFFICIAL: Record<string, StoreKind> = {
    appstore: 'appstore',
    googleplay: 'googleplay',
  };
  if (OFFICIAL[platform]) {
    return <StoreBadge store={OFFICIAL[platform]!} href={href} height={40} />;
  }

  // Fallback pill for other stores (Huawei AppGallery) that don't ship a
  // first-party badge in this component yet.
  const config: Record<
    string,
    {
      label: string;
      caption: string;
      title: string;
      icon: (props: { width: number; height: number }) => React.ReactElement;
    }
  > = {
    appgallery: {
      label: sbText('footer.appGallery.label'),
      caption: sbText('footer.appGallery.caption'),
      title: sbText('footer.appGallery.store'),
      icon: AppGalleryIcon,
    },
  };
  const c = config[platform];
  if (!c) return null;
  const Icon = c.icon;
  return (
    <PortalLink
      href={href}
      aria-label={c.label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: 'var(--space-1-5, 6px) var(--space-3, 12px)',
        borderRadius: 6,
        background: 'var(--color-neutral-950, #0a0a0a)',
        color: 'var(--color-text-on-brand, #fff)',
        textDecoration: 'none',
        border: '1px solid rgba(255,255,255,0.18)',
        minHeight: 40,
      }}
    >
      <Icon width={20} height={22} />
      <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
        <span
          style={{
            fontSize: '9px',
            opacity: 0.85,
            letterSpacing: 'var(--font-tracking-wider, 0.04em)',
          }}
        >
          {c.caption}
        </span>
        <span style={{ fontSize: 'var(--font-size-sm, 14px)', fontWeight: 600 }}>{c.title}</span>
      </span>
    </PortalLink>
  );
}

function AppGalleryIcon({ width, height }: { width: number; height: number }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width={width} height={height}>
      <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 4a2 2 0 0 1 2 2v6a2 2 0 0 1-4 0V8a2 2 0 0 1 2-2zm-5 7c0 2.21 1.79 4 4 4h2c2.21 0 4-1.79 4-4h-2a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2H7z" />
    </svg>
  );
}
